import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import track from 'utils/tracker'
import {
  AlignActions,
  Modal,
  ModalActions,
  ModalNavBar,
} from '@node-space/storybook-components/dist/Modal'
import Box from '@node-space/storybook-components/dist/Box'
import DatePicker from '@node-space/storybook-components/dist/DatePicker'
import Icon from '@node-space/storybook-components/dist/Icon'
import ResponseContainer from '@node-space/storybook-components/dist/ModalResponseContainer'
import Radio from '@node-space/storybook-components/dist/Radio'
import Text from '@node-space/storybook-components/dist/Text'
import { logError } from '@node-space/utils'
import { useFeatureFlags } from '@node-space/hooks'
import { useWalletContext } from 'hooks/context'
import { useToastContext } from 'hooks/useToastContext'
import {
  customDateValueOptions,
  customDateRadioOptions,
  exportFormatRadioOptions,
} from 'pages/WalletDetails/constants/walletTransactionsExportOptions'
import {
  sendTransactionExportToEmail,
  sendTransactionExportToEmailAllWallets,
} from 'services/TransactionService'
import { IDateValue } from 'types/types'

interface CustomDateTransactionExportModalProps {
  handleClose: () => void
  showCustomDateExportModal: boolean
  downloadAll: boolean
}

type exportFileFormatType = 'csv' | 'pdf'

const CustomDateTransactionExportModal = ({
  handleClose,
  showCustomDateExportModal,
  downloadAll,
}: CustomDateTransactionExportModalProps) => {
  const { t } = useTranslation()
  const wallet = useWalletContext()
  const addToast = useToastContext()
  const { enableCsvPdfRadioButton } = useFeatureFlags()

  // from & to date values of DatePicker input only
  const [dateValue, setDateValue] = useState<IDateValue>({
    startDate: null,
    endDate: null,
  })

  // from & to date values of other specified date range options asides "Custom date range" e.g last 30 days, previous N calendar months etc
  const exportCustomDateOptions = customDateValueOptions(dateValue?.startDate, dateValue?.endDate)
  const exportCustomDateRadioOptions = customDateRadioOptions(exportCustomDateOptions)
  const exportFormatOptions = exportFormatRadioOptions()
  const [downloadingIsInitiated, setDownloadingIsInitiated] = useState(false)
  const [sentTransactionExportToEmail, setSentTransactionExportToEmail] = useState(false)

  const { watch, setValue } = useForm({
    defaultValues: {
      selectedDateOptionName: exportCustomDateOptions?.last30Days?.name,
      selectedExportFormat: exportFormatOptions[0].value as exportFileFormatType,
    },
    mode: 'onChange',
  })
  const formValues = watch()

  const customDateFieldIsSelected =
    formValues.selectedDateOptionName === exportCustomDateOptions?.customDateRange?.name
  const customDateValueName = formValues.selectedDateOptionName
  const selectedFromDateVal = exportCustomDateOptions?.[customDateValueName]?.fromDate
  const selectedToDateVal = exportCustomDateOptions?.[customDateValueName]?.toDate
  const selectedDateRange = { from: selectedFromDateVal, to: selectedToDateVal }

  const handleTransactionExportToEmail = async () => {
    try {
      setDownloadingIsInitiated(true)

      let trackedEventTitle = ''

      if (downloadAll) {
        await sendTransactionExportToEmailAllWallets(selectedDateRange)
        trackedEventTitle = 'Download all wallets successfully'
      } else {
        const walletId = wallet?.id
        await sendTransactionExportToEmail(
          formValues.selectedExportFormat,
          walletId,
          selectedDateRange
        )
        trackedEventTitle = `Download individual wallets successfully - ${formValues.selectedExportFormat}`
      }

      // TODO(dillan.isaacs): Investigate if we need to be this granular
      // Do we need the date range included in the action or event title?
      track.Amp.track(trackedEventTitle, {
        category: 'WalletTransactionExport',
        action: `download date range ${selectedFromDateVal} to ${selectedToDateVal}`,
      })

      setSentTransactionExportToEmail(true)
      setDownloadingIsInitiated(false)
    } catch (err) {
      addToast({
        title: err.data?.errorList?.[0]?.message || t('oopsSomethingWentWrong'),
        state: 'error',
        delay: 10,
      })
      logError('Error from CustomDateTransactionExportModal - handleTransactionExportToEmail', err)
    }
  }

  const exportModalActions = [
    {
      children: t('cancel'),
      onClick: handleClose,
      secondary: true,
    },
    {
      children: t('walletsPage.exportToEmail'),
      onClick: handleTransactionExportToEmail,
      loading: downloadingIsInitiated,
      secondary: false,
    },
  ]

  return (
    <Modal
      closeOnBackgroundClick={false}
      closeModal={handleClose}
      visible={showCustomDateExportModal}
    >
      {sentTransactionExportToEmail && (
        <>
          <ResponseContainer
            headline={t('walletsPage.checkYourMail')}
            responseIcon={<Icon name="ClapIcon" color="status-success-500" size="lg" />}
            description={t('walletsPage.sendTransactionToEmailDescription', {
              exportFormat: formValues.selectedExportFormat.toUpperCase(),
            })}
          />
          <ModalActions
            actions={[
              {
                children: t('done'),
                onClick: handleClose,
                type: 'button',
                loading: downloadingIsInitiated,
              },
            ]}
            alignActions={AlignActions.CENTER}
          />
        </>
      )}
      {!sentTransactionExportToEmail && (
        <>
          <ModalNavBar
            title={t('walletsPage.exportTransactionNavBarTitle')}
            onClose={handleClose}
          />
          <Box tag="form">
            <Box flex paddingL={20} paddingT={20}>
              <Radio
                onChange={value => {
                  setValue('selectedDateOptionName', value)
                }}
                options={exportCustomDateRadioOptions}
                value={formValues.selectedDateOptionName}
              />
            </Box>
            {customDateFieldIsSelected && (
              <Box paddingL={32} paddingB={16} paddingR={32}>
                <DatePicker
                  dateRange
                  dateFieldLabel={t('walletsPage.exportDateOptions.dateRangeInputLabel')}
                  dateFieldPlaceholder={t('walletsPage.exportDateOptions.dateRangeInputLabel')}
                  dateValue={dateValue}
                  onDateValueChange={dateVal => setDateValue(dateVal)}
                  dateRangeSeparator="-"
                  maxDate={new Date()}
                  enableDateFieldEdit={false}
                />
              </Box>
            )}
            {enableCsvPdfRadioButton && !downloadAll && (
              <>
                <Box paddingL={20} paddingT={20}>
                  <Text className="ml-4">{t('walletsPage.exportFormat')}</Text>
                  <Radio
                    horizontal
                    onChange={value => {
                      setValue('selectedExportFormat', value as exportFileFormatType)
                    }}
                    options={exportFormatOptions}
                    value={formValues.selectedExportFormat}
                  />
                </Box>
              </>
            )}
            <ModalActions actions={exportModalActions} alignActions={AlignActions.RIGHT} />
          </Box>
        </>
      )}
    </Modal>
  )
}

export default React.memo(CustomDateTransactionExportModal)
